import { Injectable } from '@angular/core'

import { PageInfo } from '../../common'
import { ADMIN_API_ENDPOINTS } from '../../common/api-endpoints'
import {
  ExportRewardHistoryDto,
  FindRewardCodeDto,
  FindRewardHistoryDto,
  PageResultDto,
  RewardCodeDto,
  RewardHistoryItemDto,
  RewardHistorySummaryDto,
  RewardOptionScopeDto,
  localizedToTranslation
} from '../../dto'
import { HttpService } from '../http.service'

import { AdminChallengeMilestoneIndexService } from './admin-challenge-milestone-index.service'
import { StampHistoryExportResponseDto } from './admin-stamp-history.service'

/**
 * FR-07 — Quản lý Reward History.
 *
 * ✅ ĐÃ LIVE toàn bộ (OpenAPI 2026-08-25) — mock đã gỡ hoàn toàn.
 *
 * Map API:
 *   #4 GET  /stamp-collection/challenge-rewards/summary            → summary()
 *   #5 GET  /stamp-collection/challenge-rewards                    → list()
 *   #6 GET  /stamp-collection/challenge-rewards/export             → downloadExcel()
 *   #7 POST /stamp-collection/challenge-rewards/export-excel        → exportExcel()
 *   #8 GET  /stamp-collection/challenge-reward-options/:id/codes    → listCodes()
 */
@Injectable({
  providedIn: 'root'
})
export class AdminRewardHistoryService {

  constructor(
    private httpService: HttpService,
    private milestoneIndexService: AdminChallengeMilestoneIndexService
  ) {}

  /**
   * API #4 — FR-07-BR-01: tổng hợp quà theo Reward Tier / Reward Option.
   *
   * ⚠️ BE-MISSING B9: response **không có `discountType`**, mà
   * `RewardCodeListDialogComponent.formatDiscount()` cần nó để chọn `$` hay `%`.
   * Thiếu thì Virtual Gift FIXED_AMOUNT render `50 %` thay vì `$50.00` — **sai dữ liệu**.
   * → Đang join `discountType` từ API #10 (Challenge Detail) theo `rewardOptionId`, qua
   *   `AdminChallengeMilestoneIndexService`. Bỏ join này khi BE bổ sung `discountType`
   *   vào #4.
   *
   * ⚠️ BE không trả `redeemedCount` — tự tính `claimed + used`.
   */
  public async summary(
    challengeId: string,
    rewardOptionId?: string
  ): Promise<RewardHistorySummaryDto[]> {
    const queryParams: any = {}
    if (challengeId) queryParams.challengeId = challengeId
    if (rewardOptionId) queryParams.rewardOptionId = rewardOptionId

    const rows = await this.httpService.get<any[]>(
      `${ADMIN_API_ENDPOINTS.CHALLENGE_REWARD}/summary`,
      queryParams
    )

    const optionIndex = await this.milestoneIndexService.getRewardOptions(challengeId)

    return (rows || []).map(row => {
      const claimed = row?.claimed || 0
      const used = row?.used || 0

      return RewardHistorySummaryDto.create({
        challengeId: row?.challengeId,
        challengeName: row?.challengeName,
        challengeRewardTierId: row?.rewardTierId,
        tierPosition: row?.rewardTierPosition,
        challengeRewardOptionId: row?.rewardOptionId,
        rewardName: localizedToTranslation(row?.rewardOptionName),
        rewardType: row?.rewardType,
        notClaimed: row?.notClaimed,
        claimed,
        used,
        // BE không trả — FE tự tính (FR-07-BR-01: quà đã phát = CLAIMED + USED)
        redeemedCount: claimed + used,
        totalCount: row?.total,
        // ⚠️ BE-MISSING B9 — join từ API #10
        discountType: optionIndex.get(row?.rewardOptionId)?.discountType
      })
    })
  }

  /**
   * API #5 — FR-07-BR-05/06: chi tiết quà đã phát.
   * ⚠️ `challengeId` giờ là query param; `fromDate/toDate` → `from/to`.
   * ⚠️ BE-MISSING N5: response không có `rewardCode` ⇒ cột "Reward ID" trống.
   */
  public async list(
    params: FindRewardHistoryDto
  ): Promise<{ rows: RewardHistoryItemDto[]; count: number; pageInfo: PageInfo }> {
    const page = params.page || 1
    const pageSize = params.pageSize || 10

    const queryParams: any = {
      limit: pageSize,
      offset: (page - 1) * pageSize
    }
    if (params.challengeId) queryParams.challengeId = params.challengeId
    if (params.rewardOptionId) queryParams.rewardOptionId = params.rewardOptionId
    if (params.status) queryParams.status = params.status
    if (params.search) queryParams.search = params.search
    if (params.fromDate) queryParams.from = params.fromDate
    if (params.toDate) queryParams.to = params.toDate

    const result = await this.httpService.get<PageResultDto<any>>(
      ADMIN_API_ENDPOINTS.CHALLENGE_REWARD,
      queryParams
    )

    const rows = (result?.rows || []).map(row =>
      RewardHistoryItemDto.create({
        id: row?.rewardId,
        rewardName: localizedToTranslation(row?.rewardName),
        claimedAt: row?.claimedAt,
        customerName: row?.customerName,
        phoneNumber: row?.customerPhoneNumber
        // ⚠️ BE-MISSING N5: `rewardCode` (RWD-001) không có trong response.
      })
    )

    const pageInfo = PageInfo.create()
    pageInfo.page = page
    pageInfo.pageSize = pageSize
    pageInfo.collectionSize = result.count

    return { rows, count: result.count, pageInfo }
  }

  /**
   * API #8 — FR-07-BR-02: danh sách Reward Code của một Reward Option.
   * ⚠️ Path **bỏ hẳn `challengeId`**: `/challenge-reward-options/:id/codes`.
   * ⚠️ BE-MISSING N5: response không có `rewardCode` ⇒ cột "Reward ID" trống.
   */
  public async listCodes(
    params: FindRewardCodeDto
  ): Promise<{ rows: RewardCodeDto[]; count: number; pageInfo: PageInfo }> {
    const page = params.page || 1
    const pageSize = params.pageSize || 10

    const queryParams: any = {
      limit: pageSize,
      offset: (page - 1) * pageSize
    }
    if (params.status) queryParams.status = params.status
    if (params.search) queryParams.search = params.search
    if (params.fromDate) queryParams.from = params.fromDate
    if (params.toDate) queryParams.to = params.toDate

    const result = await this.httpService.get<PageResultDto<any>>(
      `${ADMIN_API_ENDPOINTS.CHALLENGE_REWARD_OPTION}/${params.rewardOptionId}/codes`,
      queryParams
    )

    const rows = (result?.rows || []).map(row =>
      RewardCodeDto.create({
        id: row?.rewardId,
        challengeRewardOptionId: params.rewardOptionId,
        code: row?.code,
        status: row?.status,
        rewardType: row?.rewardType,
        rewardName: row?.rewardName,
        customerId: row?.customerId,
        customerName: row?.customerName,
        phoneNumber: row?.customerPhoneNumber,
        claimedAt: row?.claimedAt,
        usedAt: row?.usedAt,
        usedById: row?.usedById,
        usedByName: row?.usedByName,
        usedMallId: row?.usedMallId,
        usedMallName: localizedToTranslation(row?.usedMallName),
        usedOfflineTenantId: row?.usedOfflineTenantId,
        usedOfflineTenantName: localizedToTranslation(row?.usedOfflineTenantName),
        transactionAmount: row?.transactionAmount,
        discountAmount: row?.discountAmount
        // ⚠️ BE-MISSING N5: `rewardCode` (RWD-001) không có trong response.
      })
    )

    const pageInfo = PageInfo.create()
    pageInfo.page = page
    pageInfo.pageSize = pageSize
    pageInfo.collectionSize = result.count

    return { rows, count: result.count, pageInfo }
  }

  /**
   * Discount **cấu hình** của Reward Option (API #10) — fallback cột Discount của popup code
   * list khi code chưa dùng. Mall / Tenant không fallback: chỉ hiện nơi code được dùng.
   */
  public async getRewardOptionScope(
    challengeId: string,
    rewardOptionId: string
  ): Promise<RewardOptionScopeDto> {
    const optionIndex = await this.milestoneIndexService.getRewardOptions(challengeId)
    const option = optionIndex.get(rewardOptionId)

    if (!option) {
      return RewardOptionScopeDto.create()
    }

    return RewardOptionScopeDto.create({
      discountType: option.discountType,
      discountValue: option.discountValue
    })
  }

  /**
   * API #7 — FR-07-BR-06: export xlsx, BE gửi file về email người dùng.
   * ⚠️ `challengeId` chuyển từ path sang body; `startDate/endDate` (Date) → `from/to` (ISO).
   */
  public async exportExcel(
    payload: ExportRewardHistoryDto
  ): Promise<StampHistoryExportResponseDto> {
    return await this.httpService.post<StampHistoryExportResponseDto>(
      `${ADMIN_API_ENDPOINTS.CHALLENGE_REWARD}/export-excel`,
      {
        email: payload.email,
        challengeId: payload.challengeId,
        rewardOptionId: payload.rewardOptionId,
        status: payload.status,
        search: payload.search,
        from: payload.startDate ? new Date(payload.startDate).toISOString() : undefined,
        to: payload.endDate ? new Date(payload.endDate).toISOString() : undefined
      }
    )
  }

  /**
   * API #6 — tải trực tiếp file XLSX (binary), cùng filter với API #5.
   *
   * ✅ ĐÃ CHỐT: giữ export qua email (`exportExcel()`, API #7) làm mặc định cho Reward
   * History vì dữ liệu có thể lớn. Hàm này (tải trực tiếp) chỉ nên dùng cho tập dữ liệu nhỏ
   * — chưa gắn vào UI, để sẵn nếu cần dùng sau.
   */
  public async downloadExcel(payload: ExportRewardHistoryDto): Promise<Blob> {
    const queryParams: any = {}
    if (payload.challengeId) queryParams.challengeId = payload.challengeId
    if (payload.rewardOptionId) queryParams.rewardOptionId = payload.rewardOptionId
    if (payload.status) queryParams.status = payload.status
    if (payload.search) queryParams.search = payload.search
    if (payload.startDate) queryParams.from = new Date(payload.startDate).toISOString()
    if (payload.endDate) queryParams.to = new Date(payload.endDate).toISOString()

    return await this.httpService.downloadBlob<Blob>(
      `${ADMIN_API_ENDPOINTS.CHALLENGE_REWARD}/export`,
      queryParams
    )
  }
}
